import { useEffect, useState } from 'react'
import { useTexte } from '../../texte'
import { rueckfrage } from '../../components/Rueckfrage'

import { nochmal, verwerfen, type OutboxEntry } from '../../lib/outbox'
import { bubbleTime } from '../../lib/format'

/**
 * Eine Nachricht, die noch auf Netz wartet.
 *
 * Sie steht dort, wo sie hingehört: unten im Verlauf, an der Stelle, an der
 * sie gleich stehen wird. Blass, mit „wartet auf Netz" statt eines Hakens —
 * ein Haken behauptete eine Zustellung, und genau die gibt es hier noch nicht.
 *
 * Zwei Knöpfe, und beide sind nötig: **Verwerfen**, weil der Text dem gehört,
 * der ihn geschrieben hat, und **Nochmal**, wenn der Server abgelehnt hat.
 * Ohne den zweiten bliebe ein Eintrag mit einer Absage für immer liegen.
 */
export function Wartend({ eintrag }: { eintrag: OutboxEntry }) {
  const bilder = useVorschauen(eintrag.bilder)

  const t = useTexte()
  return (
    <div className="bubble-row bubble-row--own">
      <div className="bubble bubble--wartet">
        {/* **Dieselbe obere Zeile wie an einer gesendeten Blase** — links
            steht hier niemand (die eigene Nachricht nennt keinen Absender),
            und der leere Platz schiebt die Angabe nach rechts. Ohne die
            Zeile stünde die Uhrzeit seit dem Wegfall des Umflusses mitten
            im Text. */}
        <span className="bubble__kopfzeile">
          <span className="bubble__wer" />
          <span className="bubble__meta">
            {bubbleTime(eintrag.angelegt)}
            <span className="bubble__wartet-marke">
              {eintrag.problem === undefined ? t.chat.wartet : t.chat.nichtAngenommen}
            </span>
          </span>
        </span>

        {bilder.length > 0 && (
          <div className="wartet-bilder">
            {bilder.map((url) => (
              <img key={url} src={url} alt="" />
            ))}
          </div>
        )}

        {eintrag.body}

        {eintrag.problem !== undefined && (
          <p className="wartet-problem">{eintrag.problem}</p>
        )}

        <div className="wartet-knoepfe">
          {eintrag.problem !== undefined && (
            <button type="button" onClick={() => void nochmal(eintrag.key)}>
              {t.chat.nochmal}
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              if (await rueckfrage(t.chat.wartendeVerwerfen, t.allgemein.verwerfen)) {
                void verwerfen(eintrag.key)
              }
            }}
          >
            {t.allgemein.verwerfen}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Adressen für die Bilder aus dem Korb — und wieder freigeben.
 *
 * Ohne das Freigeben hielte jede wartende Nachricht ihre Fotos im Speicher,
 * solange die App läuft. Auf einem Telefon mit fünf wartenden Beiträgen ist
 * das kein Schönheitsfehler mehr.
 */
function useVorschauen(bilder: Blob[]): string[] {
  const [urls, setUrls] = useState<string[]>([])

  useEffect(() => {
    const neu = bilder.map((blob) => URL.createObjectURL(blob))
    setUrls(neu)
    return () => neu.forEach((url) => URL.revokeObjectURL(url))
  }, [bilder])

  return urls
}
